import React from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { makeStyles } from '@material-ui/core/styles'
import ExternalLinkButton from 'components/external-link-button'
import RouterLinkButton from 'components/router-link-button'
import EventIcon from 'components/material-symbol/icons/Event'
import EventRepeatIcon from 'components/material-symbol/icons/EventRepeat'
import SettingsOutlinedIcon from 'components/material-symbol/icons/Settings'
import SubscribeDialog from './schedule/subscribe-dialog'

const COMPACT_HEADER_ACTIONS_MAX_WIDTH = 1049

const HEADER_ICON_PROPS = {
  fontSize: 'small',
  opticalSize: 20,
}

const SUBSCRIBE_ICON_PROPS = {
  ...HEADER_ICON_PROPS,
  style: { fontSize: 18, lineHeight: 1 },
}

const useStyles = makeStyles(theme => ({
  actionsRow: {
    display: 'flex',
    gap: 10,
    justifyContent: 'flex-end',
    flexWrap: 'nowrap',
    width: 'auto',
  },
  compactActions: {
    display: 'flex',
    gap: 10,
    justifyContent: 'flex-end',
    flexWrap: 'nowrap',
    width: 'auto',
  },
  button: {
    display: 'inherit',
    minWidth: 'max-content',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    '& .MuiButton-label': {
      textTransform: 'none',
    },
    '& .MuiButton-startIcon .MuiSvgIcon-root': {
      fontSize: 20,
      width: 20,
      height: 20,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  iconButton: {
    minWidth: 40,
    width: 40,
    height: 40,
    padding: 0,
    borderRadius: '50px',
    border: '1px solid #E0E0E0',
    backgroundColor: '#FFFFFF',
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#F5F5F5',
      borderColor: '#E0E0E0',
      boxShadow: 'none',
    },
    '& .MuiButton-label': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  primaryIconButton: {
    minWidth: 40,
    width: 40,
    height: 40,
    padding: 0,
    borderRadius: '50px',
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
    '& .MuiButton-label': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    '& .MuiSvgIcon-root': {
      color: theme.palette.primary.contrastText,
    },
  },
}))

const ScheduleHeaderActions = ({
  bookingUrl,
  isOnboardingComplete,
  onBookStudentClick,
  useCoachFastBookStudent = false,
}) => {
  const classes = useStyles()
  const isCompact = useMediaQuery(
    `(max-width:${COMPACT_HEADER_ACTIONS_MAX_WIDTH}px)`,
  )

  const bookStudentButton = isOnboardingComplete ? (
    useCoachFastBookStudent ? (
      <Button
        variant="contained"
        color="primary"
        className={classes.button}
        onClick={onBookStudentClick}
        data-testid="book-student"
        startIcon={<EventIcon {...HEADER_ICON_PROPS} />}
      >
        Book Student
      </Button>
    ) : (
      <ExternalLinkButton
        variant="contained"
        color="primary"
        className={classes.button}
        href={bookingUrl}
        onClick={onBookStudentClick}
        target="_self"
        data-testid="book-student"
        startIcon={<EventIcon {...HEADER_ICON_PROPS} />}
      >
        Book Student
      </ExternalLinkButton>
    )
  ) : null

  if (isCompact) {
    return (
      <Box className={classes.compactActions}>
        <SubscribeDialog
          trigger={
            <Button
              variant="outlined"
              color="default"
              className={classes.iconButton}
              aria-label="Subscribe"
            >
              <EventRepeatIcon {...SUBSCRIBE_ICON_PROPS} />
            </Button>
          }
        />
        <RouterLinkButton
          variant="outlined"
          color="default"
          to="/pga-coach/settings/availability"
          className={classes.iconButton}
          aria-label="Availability Settings"
        >
          <SettingsOutlinedIcon {...HEADER_ICON_PROPS} />
        </RouterLinkButton>
        {isOnboardingComplete ? (
          useCoachFastBookStudent ? (
            <Button
              variant="contained"
              color="primary"
              className={classes.primaryIconButton}
              onClick={onBookStudentClick}
              data-testid="book-student"
              aria-label="Book Student"
            >
              <EventIcon {...HEADER_ICON_PROPS} />
            </Button>
          ) : (
            <ExternalLinkButton
              variant="contained"
              color="primary"
              className={classes.primaryIconButton}
              href={bookingUrl}
              onClick={onBookStudentClick}
              target="_self"
              data-testid="book-student"
              aria-label="Book Student"
            >
              <EventIcon {...HEADER_ICON_PROPS} />
            </ExternalLinkButton>
          )
        ) : null}
      </Box>
    )
  }

  return (
    <Box className={classes.actionsRow}>
      <SubscribeDialog
        trigger={
          <Button
            variant="outlined"
            color="primary"
            size="medium"
            startIcon={<EventRepeatIcon {...SUBSCRIBE_ICON_PROPS} />}
            className={classes.button}
          >
            Subscribe
          </Button>
        }
      />
      <RouterLinkButton
        variant="outlined"
        color="primary"
        size="medium"
        to="/pga-coach/settings/availability"
        startIcon={<SettingsOutlinedIcon {...HEADER_ICON_PROPS} />}
        className={classes.button}
      >
        Availability Settings
      </RouterLinkButton>
      {bookStudentButton}
    </Box>
  )
}

ScheduleHeaderActions.propTypes = {
  bookingUrl: PropTypes.string,
  isOnboardingComplete: PropTypes.bool.isRequired,
  onBookStudentClick: PropTypes.func.isRequired,
  useCoachFastBookStudent: PropTypes.bool,
}

ScheduleHeaderActions.defaultProps = {
  bookingUrl: undefined,
}

export default ScheduleHeaderActions
